import {
  ButtonModule,
  ButtonVariant,
  IconName,
  InhaberEntitiesSelectors,
  LinkModule,
  RouterSelectors,
  TooltipModule,
} from '@adnova/jf-ng-components';
import {Component, inject, OnDestroy, OnInit} from '@angular/core';
import {combineLatest, Subject, takeUntil} from 'rxjs';
import {Action, Store} from '@ngrx/store';
import {AppState} from '../../../store/states/app.state';
import {NavigationLink} from './navigation-link.interface';
import {AppNavigationSelectors} from './+store/app-navigation.selectors';
import {CommonModule} from '@angular/common';
import {MatButtonModule} from '@angular/material/button';
import {MatFormFieldModule} from '@angular/material/form-field';
import {Router, RouterLink, RouterLinkActive} from '@angular/router';
import {MatDividerModule} from '@angular/material/divider';
import {filter} from 'rxjs/operators';
import {MatomoTrackerService} from '../../../services/matomo-tracker.service';
import {UserViewConfigSelectors} from '../../../store/selector/user-view-config.selectors';


@Component({
  selector: 'app-navigation',
  templateUrl: './app-navigation.component.html',
  styleUrls: ['./app-navigation.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    MatButtonModule,
    MatFormFieldModule,
    RouterLink,
    MatDividerModule,
    RouterLinkActive,
    ButtonModule,
    LinkModule,
    TooltipModule,
  ],
})
export class AppNavigationComponent implements OnInit, OnDestroy {
  private _store = inject(Store<AppState>);
  private _router = inject(Router);
  private readonly matomoTrackerService = inject(MatomoTrackerService);
  protected readonly trackMatomoEvent = this.matomoTrackerService.trackMatomoEvent;

  private readonly _destroyed$ = new Subject<void>();
  private _buttonAction?: Action;
  private _buttonLabel = '';
  private _buttonIcon?: IconName;
  private _buttonVariant: ButtonVariant;
  private _buttonEnabled = false;
  private _navigationLinks: NavigationLink[] = [];
  protected areWeOnNewsArticlePage = false;
  protected inhaberId?: string;
  protected currentRouteClass = '';

  get buttonAction(): Action | undefined {
    return this._buttonAction;
  }

  set buttonAction(value: Action | undefined) {
    this._buttonAction = value;
  }

  get buttonLabel(): string {
    return this._buttonLabel;
  }

  set buttonLabel(value: string) {
    this._buttonLabel = value;
  }

  get buttonIcon(): IconName | undefined {
    return this._buttonIcon;
  }

  set buttonIcon(value: IconName | undefined) {
    this._buttonIcon = value;
  }

  get buttonVariant(): ButtonVariant {
    return this._buttonVariant;
  }

  set buttonVariant(value: ButtonVariant) {
    this._buttonVariant = value;
  }

  get buttonEnabled(): boolean {
    return this._buttonEnabled;
  }

  set buttonEnabled(value: boolean) {
    this._buttonEnabled = value;
  }

  get navigationLinks(): NavigationLink[] {
    return this._navigationLinks;
  }

  set navigationLinks(value: NavigationLink[]) {
    this._navigationLinks = value;
  }

  ngOnInit(): void {
    this.listenToCurrentUrl();

    this._store.select(AppNavigationSelectors.buttonEnabled).pipe(
      takeUntil(this._destroyed$),
    ).subscribe((buttonEnabled) => {
      this.buttonEnabled = buttonEnabled;
    });

    this._store.select(RouterSelectors.dataByKey('buttonLabel')).pipe(
      takeUntil(this._destroyed$),
    ).subscribe((buttonLabel) => {
      this.buttonLabel = buttonLabel || '';
    });

    this._store.select(RouterSelectors.dataByKey<Action>('buttonAction')).pipe(
      takeUntil(this._destroyed$),
    ).subscribe((buttonAction) => {
      this.buttonAction = buttonAction;
    });

    this._store.select(RouterSelectors.dataByKey<IconName>('buttonIcon')).pipe(
      takeUntil(this._destroyed$),
    ).subscribe((buttonIcon) => {
      this.buttonIcon = buttonIcon;
    });

    this._store.select(RouterSelectors.dataByKey<ButtonVariant>('buttonVariant')).pipe(
      takeUntil(this._destroyed$),
    ).subscribe((buttonVariant) => {
      this.buttonVariant = buttonVariant;
    });

    combineLatest([
      this._store.select(RouterSelectors.dataByKey<NavigationLink[]>('navigationLinks')),
      this._store.select(UserViewConfigSelectors.userViewConfigState),
    ]).pipe(
      takeUntil(this._destroyed$),
    ).subscribe(([navigationLinksFromRoute, viewConfig]) => {
      const {news, weather, agrarcharts} = viewConfig;

      const hasNews = news?.show === true;
      const hasWeather = weather?.show === true;
      const hasCharts = agrarcharts?.show === true;

      const showOverview = hasNews && (hasWeather || hasCharts);

      const filteredLinks: NavigationLink[] = (navigationLinksFromRoute || []).filter(link => {
        switch (link.route) {
          case 'news':
            return hasNews;
          case 'weather':
            return hasWeather;
          case 'agrarcharts':
            return hasCharts;
          case 'overview':
            return showOverview;
          default:
            return true;
        }
      });

      this.navigationLinks = filteredLinks;
    });


    combineLatest([
      this._store.select(InhaberEntitiesSelectors.currentInhaberId),
      this._store.select(InhaberEntitiesSelectors.refInhaberId),
      this._store.select(InhaberEntitiesSelectors.fullInhaberList),
    ]).pipe(
      takeUntil(this._destroyed$),
    ).subscribe(([currentInhaberId, refInhaberId, fullInhaberList]) => {
      if (currentInhaberId) {
        this.inhaberId = currentInhaberId;
      } else if (refInhaberId) {
        this.inhaberId = refInhaberId;
      } else if (fullInhaberList.length > 0) {
        this.inhaberId = fullInhaberList[0].id;
      }
    });
  }

  ngOnDestroy(): void {
    this._destroyed$.next();
    this._destroyed$.complete();
  }

  protected doButtonAction(): void {
    if (this._buttonAction) {
      this._store.dispatch(this._buttonAction);
    }
  }

  private listenToCurrentUrl() {
    this._store.select(RouterSelectors.url).pipe(
      takeUntil(this._destroyed$),
      filter((url): url is string => typeof url === 'string' && url.trim().length > 0)
    ).subscribe(url => {
      this.areWeOnNewsArticlePage = /^\/news\/\d+$/.test(url); // INFO: check if URL matches /news/:id (with digits)
      this.currentRouteClass = url.split('/')[1] || '';
    });
  }

  protected onEditClick() {
    this._router.navigate(['/user-view-config']);
  }
}
